import { faArrowRight, faGripVertical, faSearch, faTimes, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { set } from "immer/dist/internal";
import React, { useCallback, useEffect, useMemo, useState, useRef, createRef } from "react";
import { confirmAlert } from "react-confirm-alert";
import DraggableList from "react-draggable-list";
import { useTranslation } from "react-i18next";
import { toast, useToast } from "react-toastify";
import { Button, Col, Input, InputGroup, InputGroupText, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import { useGetAllCustomersQuery } from "../../../../../backoffice/apis/customerAPI";
import { useGetDepartmentCustomersQuery, useUpdateCustomerOrderMutation } from "../../../../../backoffice/apis/groupsAPI";
import { Customer } from "../../../../../backoffice/dtos/customer/customer";
import { Group } from "../../../../../backoffice/dtos/groups/group";
import GroupCustomer from "../../../../../backoffice/dtos/groups/GroupCustomer";

export default function DepartmentCustomersSettings(
    {
        toggle,
        group
    } : {
        toggle: () => void
        group: Group
    }
) {
    const { t } = useTranslation(['department_info','common'])

    const { data: customersFetched, isFetching: customersAreLoading, error: customersError } = useGetAllCustomersQuery({ query: "", includeDeleted: false }, { refetchOnMountOrArgChange: true });
    const { data: groupCustomers, isFetching: groupCustomersAreLoading, error: groupCustomersError } = useGetDepartmentCustomersQuery(group, { refetchOnMountOrArgChange: true });

    let modalBody: JSX.Element;
    if (customersAreLoading || groupCustomersAreLoading) {
        modalBody = (
            <>
                <ModalBody className="d-flex flex-row justify-content-center">
                    <Spinner className="m-2"/>
                </ModalBody>
            </>
        );
    } else if (customersError || groupCustomersError) {
        modalBody = (
            <>
                <ModalBody>
                    Failed to fetch data
                </ModalBody>
            </>
        );
    } else {
        modalBody = (
            <InnerDepartmentCustomersSettings toggle={toggle} group={group} customers={customersFetched!!} currentGroupCustomers={groupCustomers!!} />
        );
    }

    return (
        <Modal toggle={toggle} size="xl" isOpen={true}>
            <ModalHeader toggle={toggle}>
                {t("Department customers")}
            </ModalHeader>
            {modalBody}
        </Modal>
    )
}

function InnerDepartmentCustomersSettings(
    {
        toggle,
        group,
        customers,
        currentGroupCustomers
    } : {
        toggle: () => void,
        group: Group,
        customers: Customer[]
        currentGroupCustomers: GroupCustomer[]
    }
): JSX.Element {
    const { t } = useTranslation(['department_info','common'])
    const [groupCustomers, setGroupCustomers] = useState(Array<CustomerItem>())
    const [filter, setFilter] = useState('')
    const [activeFilter, setActiveFilter] = useState('')
    const [updateDepartmentCustomerOrder] = useUpdateCustomerOrderMutation();
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        const items = currentGroupCustomers.filter(m => m.isActive).map<CustomerItem>(m => new CustomerItem(m.customerId, m.customerName, true, setGroupCustomers))
        setGroupCustomers(items);
    }, [currentGroupCustomers, customers])

    const handleUpdate = useCallback(async () => {
        try {
            setLoading(true)

            const customers = groupCustomers.map(m => m.id);

            await updateDepartmentCustomerOrder({ groupId: group.id, customers: customers }).unwrap()
            toggle()
        } catch(error) {
            toast("Could not update order.")
        } finally {
            setLoading(false)
        }
    }, [groupCustomers]);

    const askToUpdate = useCallback(() => {
        confirmAlert({
          title: t("Update department's customers"),
          message: t("Are you sure you want to update the department's customers?"),
          buttons: [{ label: t('yes',{ns:'common'}), onClick:() => handleUpdate() }, { label: t('no',{ns:'common'}) }]
        })
    }, [handleUpdate]);

    const unusedCustomers = useMemo(() => {
        const currentCustomers = new Set(groupCustomers.map(i => i.id))
        return customers.filter(c => !currentCustomers.has(c.id) && c.description.includes(filter)).map<CustomerItem>(m => new CustomerItem(m.id, m.description, false, setGroupCustomers))
    }, [groupCustomers, filter, customers]);

    return (
        <>
            <ModalBody >
                <Row>
                    <Col md='6'>
                        <InputGroup className="mb-3">
                            <Input className="form-control" placeholder= {t('search',{ns:'common'})} value={filter} onChange={(it: any) => setFilter(it.target.value)}/>
                            { filter.length > 0
                                ? <Button color="danger" onClick={() => setFilter('')}><FontAwesomeIcon icon={faTimes}/></Button>
                                : <InputGroupText><FontAwesomeIcon icon={faSearch}/></InputGroupText>
                            }
                        </InputGroup>
                    </Col>
                    <Col md='6'>
                        <InputGroup className="mb-3">
                            <Input className="form-control" placeholder= {t('search',{ns:'common'})} value={activeFilter} onChange={(it: any) => setActiveFilter(it.target.value)}/>
                            { filter.length > 0
                                ? <Button color="danger" onClick={() => setFilter('')}><FontAwesomeIcon icon={faTimes}/></Button>
                                : <InputGroupText><FontAwesomeIcon icon={faSearch}/></InputGroupText>
                            }
                        </InputGroup>
                    </Col>
                </Row>
                <Row>
                    <Col md='6'>
                        <div className="d-flex flex-column flex-fill overflow-auto" style={{height: 500}}>
                            {
                                unusedCustomers.map(
                                    it => (
                                        <UnscrollableCustomerItem {...it} />
                                    )
                                )
                            }
                        </div>
                    </Col>
                    <Col md='6'>
                        {
                            activeFilter.length > 0 ? (
                                <div className="d-flex flex-column flex-fill overflow-auto" style={{height: 500}}>
                                    {
                                        groupCustomers.filter(m => m.name.includes(activeFilter)).map(
                                            it => (
                                                <UnscrollableCustomerItem {...it}/>
                                            )
                                        )
                                    }
                                </div>
                            ) : (
                                <DraggableListWithContainer groupCustomers={groupCustomers} setGroupCustomers={setGroupCustomers}/>
                            )
                        }
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button color="success" onClick={askToUpdate} disabled={loading}>
                     {loading && <Spinner size="sm"/> } {t('update',{ns:'common'})}
                </Button>{' '}
                <Button color="danger" onClick={toggle} disabled={loading}>
                    {loading && <Spinner size="sm"/> } {t('cancel',{ns:'common'})}
                </Button>
            </ModalFooter>
        </>
    );

}

class DraggableListProps {
    groupCustomers: CustomerItem[];
    setGroupCustomers: React.Dispatch<React.SetStateAction<CustomerItem[]>>;

    constructor(groupCustomers: CustomerItem[], setGroupCustomers: React.Dispatch<React.SetStateAction<CustomerItem[]>>) {
        this.groupCustomers = groupCustomers;
        this.setGroupCustomers = setGroupCustomers;
    }
}

class DraggableListWithContainer extends React.Component<DraggableListProps, CustomerState> {
    private _container = React.createRef<HTMLDivElement>();

    render() {
        return (
            <div style={{height: 500, overflow: 'auto'}} ref={this._container}>
                <DraggableList<CustomerItem, void, ActiveCustomerItem>
                    itemKey="id"
                    template={ActiveCustomerItem}
                    list={this.props.groupCustomers}
                    onMoveEnd={list => this.props.setGroupCustomers(list as CustomerItem[])}
                    container={() => this._container.current}
                />
            </div>
        )
    }
}

class CustomerItem {
    id: number;
    name: string;
    active: boolean;
    setGroupCustomers: React.Dispatch<React.SetStateAction<CustomerItem[]>>;
    constructor(id: number, name: string, active: boolean, setGroupCustomers: React.Dispatch<React.SetStateAction<CustomerItem[]>>) {
        this.id = id;
        this.name = name;
        this.active = active;
        this.setGroupCustomers = setGroupCustomers;
    }
    remove = () => {
        this.active = false;
        this.setGroupCustomers(ref => {
            const newItems = []
            for (const item of ref) {
                if (item.id !== this.id) {
                    newItems.push(item)
                }
            }
            return newItems
        })
    }
    add = () => {
        this.active = true;
        this.setGroupCustomers(ref => {
            const newItems = []
            let hasItem = false
            for (const item of ref) {
                if (item.id === this.id) {
                    hasItem = true;
                    break;
                }
                newItems.push(item)
            }
            if (!hasItem) {
                newItems.push(this)
            }
            return newItems
        })
    }
}

interface CustomerProps {
    item: CustomerItem;
    itemSelected: number;
    dragHandleProps: object;
}

interface CustomerState {
    value: number;
}

class ActiveCustomerItem extends React.Component<CustomerProps, CustomerState> {
    state = {
        value: 0,
    };

    _inc() {
        this.setState({
            value: this.state.value + 1,
        });
    }

    render() {
        return (
            <Row>
                <Col lg='auto' className="d-flex flex-row align-items-center">
                    <div className="dragHandle" {...this.props.dragHandleProps}>
                        <FontAwesomeIcon  icon={faGripVertical} />
                    </div>
                </Col>
                <Col className="d-flex flex-row align-items-center">
                    <h5>{this.props.item.name}</h5>
                </Col>
                <Col lg='auto' className="d-flex justify-content-end">
                    <Button color="danger" onClick={this.props.item.remove}><FontAwesomeIcon icon={faXmark} /></Button>
                </Col>
            </Row>
        )
    }
}

function UnscrollableCustomerItem(
    props: CustomerItem
) {
    return (
        <Row className="mb-2">
            <Col className="d-flex flex-row align-items-center">
                <h5>{props.name}</h5>
            </Col>
            {
                props.active ? (
                    <Col lg='auto' className="d-flex justify-content-end">
                        <Button color="danger" onClick={props.remove}><FontAwesomeIcon icon={faXmark} /></Button>
                    </Col>
                ) : (
                    <Col lg='auto' className="d-flex justify-content-end">
                        <Button color="success" onClick={props.add}><FontAwesomeIcon icon={faArrowRight} /></Button>
                    </Col>
                )
            }
        </Row>
    )
}
